import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import BasicMap from "components/BasicMap";
import { tabsOffset } from "components/TabLinks";
import { haversineDistanceMiles } from "encamp-shared/src/geo/distance";

import { useFacility } from "../useFacility";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const LOCAL_AGENCIES_SECTION_ID = "local-agencies";
export const LOCAL_AGENCIES_SECTION_ICON = "📍";

function buildAddressLines(
  street1?: string | null,
  street2?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null,
  country?: string | null
) {
  const lines: string[] = [];
  const streetLine = [street1, street2].filter(Boolean).join(", ");
  if (streetLine) lines.push(streetLine);
  const cityStateZip = [[city, state].filter(Boolean).join(" "), zip].filter(Boolean).join(" ");
  if (cityStateZip) lines.push(cityStateZip);
  if (country) lines.push(country);
  return lines;
}

interface AgencyDisplayProps {
  label: string;
  name?: string | null;
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  facilityLatitude?: number | null;
  facilityLongitude?: number | null;
}

function AgencyDisplay({
  label,
  name,
  street1,
  street2,
  city,
  state,
  zip,
  country,
  latitude,
  longitude,
  facilityLatitude,
  facilityLongitude,
}: AgencyDisplayProps) {
  const hasCoordinates =
    latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude);

  const addressLines = buildAddressLines(street1, street2, city, state, zip, country);

  const hasFacilityCoordinates =
    facilityLatitude != null &&
    facilityLongitude != null &&
    !Number.isNaN(facilityLatitude) &&
    !Number.isNaN(facilityLongitude);

  const distanceMiles =
    hasCoordinates && hasFacilityCoordinates
      ? haversineDistanceMiles(
          { latitude: facilityLatitude, longitude: facilityLongitude },
          { latitude: latitude!, longitude: longitude! }
        )
      : null;

  return (
    <Box
      sx={{
        position: "relative",
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <IconButton
        size="small"
        className="edit-icon"
        sx={{ color: "action.selected", position: "absolute", top: 0, right: 0, mr: 2 }}
      >
        <Edit fontSize="small" />
      </IconButton>
      <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
        {/* Map and distance */}
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={{ width: "240px", height: "196px" }}>
            {hasCoordinates ? (
              <BasicMap latitude={latitude} longitude={longitude} static />
            ) : (
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No location available.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
          {distanceMiles != null && (
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              {distanceMiles.toFixed(1)} miles from the facility
            </Typography>
          )}
        </Box>

        {/* Agency info */}
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Primary: this agency will be included in your report in the portal.
            </Typography>
          </Box>
          <Box>
            {addressLines.map((line, i) => (
              <Typography key={i} variant="body1">
                {line}
              </Typography>
            ))}
            {hasCoordinates && (
              <Typography variant="caption" color="text.secondary">
                (Shown on map)
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}

export const LocalAgenciesSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;

  const lepc = facility?.lepc;
  const fd = facility?.fireDepartment;
  const isCA = facility?.state === "CA";

  return (
    <Box
      id={LOCAL_AGENCIES_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">
          {LOCAL_AGENCIES_SECTION_ICON} Local Agencies (Chemicals)
        </Typography>
      </Stack>

      <Stack spacing={4} pl={2}>
        {/* LEPC / CUPA heading */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">
            {isCA
              ? "Certified Unified Program Agency (CUPA)"
              : "Local Emergency Planning Committee (LEPC)"}
          </Typography>
          <Button size="small">{isCA ? "ADD CUPA" : "ADD LEPC"}</Button>
        </Stack>

        <AgencyDisplay
          label={isCA ? "Primary CUPA" : "Primary LEPC"}
          name={lepc?.name}
          street1={lepc?.mailingAddress}
          street2={lepc?.mailingAddressLine2}
          city={lepc?.city}
          state={lepc?.state}
          zip={lepc?.zip}
          country="United States"
          latitude={lepc?.latitude}
          longitude={lepc?.longitude}
          facilityLatitude={facility?.latitude}
          facilityLongitude={facility?.longitude}
        />

        {/* Fire Department — not shown for CA */}
        {!isCA && (
          <>
            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Fire Department</Typography>
              <Button size="small">ADD FIRE DEPT</Button>
            </Stack>

            <AgencyDisplay
              label="Primary Fire Department"
              name={fd?.name}
              street1={fd?.mailingAddress}
              street2={fd?.mailingAddressLine2}
              city={fd?.city}
              state={fd?.state}
              zip={fd?.zip}
              country="United States"
              latitude={fd?.latitude}
              longitude={fd?.longitude}
              facilityLatitude={facility?.latitude}
              facilityLongitude={facility?.longitude}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};
